/**
 * 인증된 사용자가 보는 콘솔 레이아웃 — 좌측 사이드바 + 우측 메인.
 *
 * 사이드바는 desktop 의 사이드바와 톤 일관 (작은 로고 + 한국어 라벨 + lucide
 * 아이콘). 모바일 대응은 후속 — 우선 데스크톱 폭(>= md) 기준.
 */
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  CircleDollarSign,
  CreditCard,
  Download,
  FolderGit2,
  LayoutDashboard,
  LogOut,
  Package,
  UserCog,
  Users,
  type LucideIcon,
} from "lucide-react";
import { EmailVerificationBanner } from "@/components/email-verification-banner";
import { EmailVerificationGate } from "@/components/email-verification-gate";
import { WorkspaceSwitcher } from "@/components/workspace-switcher";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/stores/auth-store";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/app", label: "대시보드", icon: LayoutDashboard, end: true },
  { to: "/app/organizations", label: "조직", icon: Building2 },
  { to: "/app/projects", label: "프로젝트", icon: FolderGit2 },
  { to: "/app/usage", label: "사용량", icon: BarChart3 },
  { to: "/app/billing", label: "결제 / 플랜", icon: CreditCard },
  { to: "/app/account", label: "계정 설정", icon: UserCog },
  { to: "/download", label: "데스크톱 앱", icon: Download },
  { to: "/app/admin/users", label: "유저 관리", icon: Users, adminOnly: true },
  { to: "/app/admin/releases", label: "릴리스 관리", icon: Package, adminOnly: true },
  { to: "/app/admin/pricing", label: "가격 관리", icon: CircleDollarSign, adminOnly: true },
];

export function ConsoleLayout(): JSX.Element {
  const navigate = useNavigate();
  const account = useAuthStore((s) => s.account);
  const signout = useAuthStore((s) => s.signout);

  const handleSignout = (): void => {
    signout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)]">
      <aside className="flex w-60 shrink-0 flex-col border-r border-border bg-muted/30">
        <div className="px-3 pt-4">
          <WorkspaceSwitcher />
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4 text-sm">
          {NAV_ITEMS.filter(
            (item) => !item.adminOnly || account?.isSuperAdmin === true,
          ).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/80 hover:bg-accent hover:text-accent-foreground",
                )
              }
            >
              <item.icon className="h-4 w-4" aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="mb-2 px-3 text-xs">
            <div className="font-medium">
              {account?.name?.trim() || account?.email || "—"}
            </div>
            {account?.name && (
              <div className="text-muted-foreground">{account.email}</div>
            )}
          </div>
          <button
            type="button"
            onClick={handleSignout}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground/80 transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            로그아웃
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        {account?.emailVerified === false ? (
          // 미인증이면 본문 대신 게이트. 백엔드에서도 거의 모든 endpoint 가
          // 403 으로 막혀 있어 빈 화면이 나오므로 명확한 안내가 더 친절.
          <EmailVerificationGate />
        ) : (
          <>
            <EmailVerificationBanner />
            <Outlet />
          </>
        )}
      </main>
    </div>
  );
}
